/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useParcels } from "../../store/useParcels";
import { useDropLocations } from "../../store/useDropLocations";
import {
  sortLocationNames,
  getOrderedLabelsFromFilterPlaces,
} from "../../utils/locationOrdering";
import { Tooltip } from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import {
  MaterialsChartFilters,
  getDefaultMaterialsChartFilters,
  type MaterialsChartFiltersState,
} from "../../pages/Volumes/MaterialsChartFilters";
import { getParcelCourierColor } from "../../constants/plasticColors";

dayjs.extend(isoWeek);

interface ParcelVolumesPerCourierProps {
  filterType?: "location" | "city";
  filterPlaces?: string[];
  sequenceStep?: number;
  setSequenceStep?: (n: number) => void;
  sequenceStepIndex?: number;
}

export const ParcelVolumesPerCourier = React.memo(
  ({
    filterType,
    filterPlaces,
    sequenceStep,
    setSequenceStep,
    sequenceStepIndex,
  }: ParcelVolumesPerCourierProps) => {
    const useGeneralFilter =
      filterType !== undefined && filterPlaces !== undefined;
    const snapshotRef = useRef(null);
    const {
      parcelVolumesPerCourierPerLocation,
      getParcelVolumesPerCourierPerLocation,
    } = useParcels();
    const { getDropLocations, dropLocations } = useDropLocations();

    const [_, takeScreenshot] = useScreenshot();
    const [filters, setFilters] = useState<MaterialsChartFiltersState>(() =>
      getDefaultMaterialsChartFilters()
    );
    const [isCsvDownloading, setIsCsvDownloading] = useState(false);

    const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";
    const realPlaces =
      filterPlaces?.filter((p) => p !== TOTAL_ALL_STORES_ID) ?? [];

    const allCourierNames = useMemo(() => {
      const data = parcelVolumesPerCourierPerLocation;
      if (!data?.courierSeries?.length) return [];
      return data.courierSeries.map((s) => s.name).sort();
    }, [parcelVolumesPerCourierPerLocation]);

    const { orderedLocationLabels, orderedSeries } = useMemo(() => {
      const data = parcelVolumesPerCourierPerLocation;
      const apiLabels = data?.locationLabels ?? [];
      const baseSeries = data?.courierSeries ?? [];
      if (!apiLabels.length || !baseSeries.length) {
        return {
          orderedLocationLabels: apiLabels,
          orderedSeries: baseSeries,
        };
      }

      const userOrderedLabels = getOrderedLabelsFromFilterPlaces(
        filterPlaces ?? [],
        filterType ?? "location",
        dropLocations,
        TOTAL_ALL_STORES_ID
      );
      const apiLabelsSet = new Set(apiLabels);
      const orderedLabels =
        userOrderedLabels.length > 0
          ? userOrderedLabels.filter((l) => apiLabelsSet.has(l))
          : sortLocationNames([...apiLabels], dropLocations);

      const orderedSeries = baseSeries.map((s) => {
        const reorderedData = orderedLabels.map(
          (label) => s.data[apiLabels.indexOf(label)] ?? 0
        );
        return { ...s, data: reorderedData };
      });

      return {
        orderedLocationLabels: orderedLabels,
        orderedSeries,
      };
    }, [
      parcelVolumesPerCourierPerLocation,
      filterPlaces,
      filterType,
      dropLocations,
    ]);

    const hasData = useMemo(() => {
      if (!parcelVolumesPerCourierPerLocation?.locationLabels?.length)
        return false;
      return orderedSeries.some((s) => s.data.some((v) => v > 0));
    }, [parcelVolumesPerCourierPerLocation, orderedSeries]);

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "parcel_volumes_per_courier.png";
        link.click();
      });
    };

    const downloadCsv = () => {
      setIsCsvDownloading(true);
      const params: Record<string, string> = {
        period: "daily",
        startYear: filters.fromYear.toString(),
        endYear: filters.toYear.toString(),
      };
      if (filters.period === "month") {
        params.startMonth = filters.fromMonth.toString();
        params.endMonth = filters.toMonth.toString();
      } else if (filters.period === "quarter") {
        params.startMonth = String((filters.fromQuarter - 1) * 3 + 1);
        params.endMonth = String(filters.toQuarter * 3);
      } else if (filters.period === "year") {
        params.startMonth = "1";
        params.endMonth = "12";
      } else {
        const startDate = dayjs()
          .year(filters.fromYear)
          .isoWeek(filters.fromWeek)
          .startOf("isoWeek");
        const endDate = dayjs()
          .year(filters.toYear)
          .isoWeek(filters.toWeek)
          .endOf("isoWeek");
        params.startYear = startDate.year().toString();
        params.startMonth = (startDate.month() + 1).toString();
        params.endYear = endDate.year().toString();
        params.endMonth = (endDate.month() + 1).toString();
      }
      if (useGeneralFilter) {
        params.type = filterType ?? "location";
        if (realPlaces.length > 0) {
          params.places = realPlaces.join(",");
        }
      }

      axios
        .get("/parcels/stats/parcel-volumes-per-courier-per-location", {
          params,
        })
        .then((response) => {
          let labels = (response.data?.locationLabels as string[]) ?? [];
          let series =
            (response.data?.courierSeries as { name: string; data: number[] }[]) ??
            [];

          if (filters.period === "week") {
            const startDate = dayjs()
              .year(filters.fromYear)
              .isoWeek(filters.fromWeek)
              .startOf("isoWeek");
            const endDate = dayjs()
              .year(filters.toYear)
              .isoWeek(filters.toWeek)
              .endOf("isoWeek");
            const allowed = new Set<string>();
            const cursor = startDate.clone();
            while (cursor.isBefore(endDate) || cursor.isSame(endDate, "day")) {
              allowed.add(cursor.format("YYYY-MM-DD"));
              cursor.add(1, "day");
            }
            const keepIndexes = labels
              .map((label, idx) => (allowed.has(label) ? idx : -1))
              .filter((idx) => idx >= 0);
            labels = keepIndexes.map((idx) => labels[idx]);
            series = series.map((item) => ({
              ...item,
              data: keepIndexes.map((idx) => item.data[idx] ?? 0),
            }));
          }

          const header =
            "Date," + series.map((s) => s.name).join(",") + "\n";
          const rows = labels.map((label, idx) => {
            const row = [label];
            series.forEach((s) => {
              row.push(String(s.data[idx] ?? 0));
            });
            return row.join(",");
          });
          const csv = header + rows.join("\n");
          const blob = new Blob([csv], {
            type: "text/csv;charset=utf-8;",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `parcel_volumes_per_courier_daily_${dayjs().format(
            "YYYY-MM-DD"
          )}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Error downloading CSV:", error);
        })
        .finally(() => {
          setIsCsvDownloading(false);
        });
    };

    useEffect(() => {
      getDropLocations();
    }, []);

    const prevFilterSigRef = useRef<string | null>(null);

    useEffect(() => {
      if (
        sequenceStep !== undefined &&
        setSequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep < sequenceStepIndex
      ) {
        return;
      }
      const filterSig = JSON.stringify({
        filters,
        filterType,
        filterPlaces,
      });
      if (
        sequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep > sequenceStepIndex &&
        prevFilterSigRef.current === filterSig
      ) {
        return;
      }
      prevFilterSigRef.current = filterSig;

      const typeParam = useGeneralFilter
        ? (filterType ?? "location")
        : undefined;
      const placesParam =
        useGeneralFilter && realPlaces.length > 0 ? realPlaces : undefined;
      const {
        period,
        fromYear,
        toYear,
        fromMonth,
        toMonth,
        fromQuarter,
        toQuarter,
        fromWeek,
        toWeek,
      } = filters;
      let startMonth: number;
      let endMonth: number;
      if (period === "month") {
        startMonth = fromMonth;
        endMonth = toMonth;
      } else if (period === "quarter") {
        startMonth = (fromQuarter - 1) * 3 + 1;
        endMonth = toQuarter * 3;
      } else if (period === "year") {
        startMonth = 1;
        endMonth = 12;
      } else {
        const startOfFromWeek = dayjs()
          .year(fromYear)
          .isoWeek(fromWeek)
          .startOf("isoWeek");
        const endOfToWeek = dayjs()
          .year(toYear)
          .isoWeek(toWeek)
          .endOf("isoWeek");
        startMonth = startOfFromWeek.month() + 1;
        endMonth = endOfToWeek.month() + 1;
      }
      const promise = getParcelVolumesPerCourierPerLocation(
        fromYear,
        startMonth,
        toYear,
        endMonth,
        typeParam,
        placesParam
      );
      if (
        setSequenceStep != null &&
        sequenceStepIndex != null &&
        sequenceStep === sequenceStepIndex
      ) {
        Promise.resolve(promise).finally(() =>
          setSequenceStep(sequenceStepIndex + 1)
        );
      }
    }, [
      filters,
      getParcelVolumesPerCourierPerLocation,
      useGeneralFilter,
      filterType,
      sequenceStep,
      setSequenceStep,
      sequenceStepIndex,
      filterPlaces,
    ]);

    const chartOptions = useMemo(
      () => ({
        chart: {
          type: "bar" as const,
          stacked: true,
          zoom: { enabled: false },
          toolbar: { show: false },
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["#fff"],
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "55%",
            borderRadius: 4,
            dataLabels: { position: "center" as const },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) =>
            val === 0
              ? ""
              : new Intl.NumberFormat("nl-NL", {
                  style: "decimal",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(val),
          style: {
            fontSize: "11px",
            fontWeight: "bold",
            colors: ["#ffffff"],
          },
        },
        xaxis: {
          categories: orderedLocationLabels,
          title: {
            text: "Store",
            style: { fontSize: "14px", fontWeight: "bold" },
          },
          labels: {
            style: { fontSize: "12px" },
            maxWidth: 120,
          },
        },
        yaxis: {
          title: {
            text: "Items",
            style: { fontSize: "14px", fontWeight: "bold" },
          },
          labels: {
            formatter: (val: number) =>
              new Intl.NumberFormat("nl-NL", {
                style: "decimal",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(val),
          },
        },
        grid: { strokeDashArray: 5 },
        legend: {
          show: true,
          position: "right" as const,
          fontSize: "14px",
          fontFamily: "Satoshi, sans-serif",
          labels: {
            colors: "#374151",
          },
          markers: {
            width: 12,
            height: 12,
            strokeWidth: 0,
            radius: 6,
          },
          itemMargin: {
            horizontal: 0,
            vertical: 4,
          },
        },
        colors: orderedSeries.map((s) => getParcelCourierColor(s.name)),
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val: number) =>
              new Intl.NumberFormat("nl-NL", {
                style: "decimal",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(val),
          },
        },
      }),
      [orderedLocationLabels, orderedSeries]
    );

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-end bg-red-500/00 gap-4">
          <div className="">
            <MaterialsChartFilters
              state={filters}
              onChange={setFilters}
              allowWeekly={false}
              hasData={hasData}
              onDownloadImage={downloadScreenshot}
              onDownloadCsv={downloadCsv}
              isCsvDownloading={isCsvDownloading}
            />
          </div>
        </div>

        <div ref={snapshotRef} className="mt-4">
          <div className="flex items-center mb-2 sm:mb-0">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Parcel volumes per courier
            </p>
            <Tooltip
              content="Stacked bar: each bar = store, segments = couriers."
              placeholder={undefined}
            >
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>

          <div className="mt-4 h-[450px]">
            {hasData ? (
              <Chart
                options={chartOptions}
                series={orderedSeries}
                type="bar"
                height={450}
              />
            ) : parcelVolumesPerCourierPerLocation === null ? (
              <div className="flex flex-col items-center justify-center h-[450px] w-full gap-2">
                <svg
                  aria-hidden="true"
                  className={`w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base`}
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-gray-500 font-satoshi">
                  No data for the selected filters. Select locations above.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);
